/**
 * Real backend adapter for the employer console.
 *
 * Employer auth is a separate realm from the worker's: email + password + 2FA,
 * with the active org carried in the token. Every scoped call goes through
 * `X-Pinchcrew-Org`, which the backend uses to apply row-level tenancy — a request
 * without it must be refused rather than silently returning everything.
 */
import { ApiRequestError } from './client'
import type { EmployerApi, EmployerShiftFilters } from './employer-client'
import type {
  CreateShiftInput, DashboardSummary, EmployerAssignment, EmployerShift,
  EmployerTimesheet, EmployerSignup, EmployerUser, Invoice, LedgerEntry, OrgLedger,
  OrgMembership, OrgSite, SiteQr, Page, RatingInput, ShiftCostEstimate, Skill,
  WorkerSummary,
} from './types'

const TOKEN_KEY = 'pinchcrew.employer.token'
const REFRESH_KEY = 'pinchcrew.employer.refresh'
const ORG_KEY = 'pinchcrew.employer.org'

function read(key: string): string | null {
  try { return localStorage.getItem(key) } catch { return null }
}
function write(key: string, value: string | null): void {
  try { value === null ? localStorage.removeItem(key) : localStorage.setItem(key, value) } catch { /* private mode */ }
}

export class EmployerHttpApi implements EmployerApi {
  /** Single-flight refresh — a rotated token must not log out other tabs. */
  private refreshInFlight: Promise<string> | null = null

  constructor(private readonly baseUrl: string) {}

  private async request<T>(path: string, init: RequestInit = {}, retry = true): Promise<T> {
    const headers = new Headers(init.headers)
    const token = read(TOKEN_KEY)
    const org = read(ORG_KEY)
    if (token) headers.set('Authorization', `Bearer ${token}`)
    if (org) headers.set('X-Pinchcrew-Org', org)
    if (!(init.body instanceof FormData)) headers.set('Content-Type', 'application/json')

    const res = await fetch(`${this.baseUrl}${path}`, { ...init, headers })

    if (res.status === 401 && retry && read(REFRESH_KEY)) {
      await this.refresh()
      return this.request<T>(path, init, false)
    }
    if (!res.ok) {
      let body: { code?: string; message?: string; field_errors?: Record<string, string[]> } = {}
      try { body = await res.json() } catch { /* non-JSON error */ }
      throw new ApiRequestError(
        body.code ?? 'http_error',
        body.message ?? `Request failed (${res.status})`,
        body.field_errors, res.status,
      )
    }
    if (res.status === 204) return undefined as T
    return res.json() as Promise<T>
  }

  private refresh(): Promise<string> {
    if (this.refreshInFlight) return this.refreshInFlight
    this.refreshInFlight = (async () => {
      const res = await fetch(`${this.baseUrl}/employer/auth/refresh/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh: read(REFRESH_KEY) }),
      })
      if (!res.ok) {
        write(TOKEN_KEY, null); write(REFRESH_KEY, null)
        throw new ApiRequestError('session_expired', 'Please sign in again.', undefined, 401)
      }
      const data = await res.json() as { access: string; refresh?: string }
      write(TOKEN_KEY, data.access)
      if (data.refresh) write(REFRESH_KEY, data.refresh)
      return data.access
    })().finally(() => { this.refreshInFlight = null })
    return this.refreshInFlight
  }

  private post<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>(path, { method: 'POST', body: body ? JSON.stringify(body) : undefined })
  }

  // ---- auth ----
  async login(email: string, password: string) {
    const data = await this.post<{ access: string; refresh: string; user: EmployerUser; memberships: OrgMembership[] }>(
      '/employer/auth/login/', { email, password },
    )
    write(TOKEN_KEY, data.access)
    write(REFRESH_KEY, data.refresh)
    return { user: data.user, memberships: data.memberships }
  }

  signupStart(input: EmployerSignup) {
    return this.post<{ expires_in_seconds: number }>('/employer/auth/signup/start/', input)
  }

  async signup(input: EmployerSignup) {
    const data = await this.post<{ access: string; refresh: string; user: EmployerUser; memberships: OrgMembership[] }>(
      '/employer/auth/signup/', input,
    )
    write(TOKEN_KEY, data.access)
    write(REFRESH_KEY, data.refresh)
    return { user: data.user, memberships: data.memberships }
  }

  /** True when a token survives a reload, so the app can restore the session. */
  hasStoredSession(): boolean {
    return read(TOKEN_KEY) !== null
  }

  async logout() {
    write(TOKEN_KEY, null); write(REFRESH_KEY, null); write(ORG_KEY, null)
  }

  getMe() { return this.request<{ user: EmployerUser; memberships: OrgMembership[] }>('/employer/me/') }

  async selectOrg(orgId: string) {
    write(ORG_KEY, orgId)
    return this.request<OrgMembership>(`/employer/orgs/${orgId}/`)
  }

  // ---- dashboard ----
  getDashboard() { return this.request<DashboardSummary>('/employer/dashboard/') }

  // ---- shifts ----
  listShifts(filters: EmployerShiftFilters = {}) {
    const q = new URLSearchParams()
    for (const [k, v] of Object.entries(filters)) if (v !== undefined) q.set(k, String(v))
    return this.request<Page<EmployerShift>>(`/employer/shifts/?${q}`)
  }
  getShift(id: string) { return this.request<EmployerShift>(`/employer/shifts/${id}/`) }
  estimateShift(input: CreateShiftInput) { return this.post<ShiftCostEstimate>('/employer/shifts/estimate/', input) }
  createShift(input: CreateShiftInput) { return this.post<EmployerShift[]>('/employer/shifts/', input) }
  async cancelShift(id: string, reason: string) { await this.post(`/employer/shifts/${id}/cancel/`, { reason }) }
  async releaseAssignment(shiftId: string, assignmentId: string, reason: string) {
    await this.post(`/employer/shifts/${shiftId}/assignments/${assignmentId}/release/`, { reason })
  }

  // ---- timesheets ----
  listTimesheets(status?: string) {
    return this.request<EmployerTimesheet[]>(`/employer/timesheets/${status ? `?status=${status}` : ''}`)
  }
  approveTimesheet(id: string) { return this.post<EmployerTimesheet>(`/employer/timesheets/${id}/approve/`) }
  approveAllPending() { return this.post<{ approved: number }>('/employer/timesheets/approve-all/') }
  adjustTimesheet(id: string, paidMinutes: number, reason: string) {
    return this.post<EmployerTimesheet>(`/employer/timesheets/${id}/adjust/`, { paid_minutes: paidMinutes, reason })
  }

  // ---- worker pool ----
  listWorkers(scope: 'all' | 'favourites' | 'blocked' = 'all') {
    return this.request<WorkerSummary[]>(`/employer/workers/?scope=${scope}`)
  }
  favouriteWorker(workerId: string, on: boolean) {
    return this.post<WorkerSummary>(`/employer/workers/${workerId}/favourite/`, { on })
  }
  blockWorker(workerId: string, on: boolean, reason?: string) {
    return this.post<WorkerSummary>(`/employer/workers/${workerId}/block/`, { on, reason })
  }
  rateAssignment(assignmentId: string, rating: RatingInput) {
    return this.post<EmployerAssignment>(`/employer/assignments/${assignmentId}/rate/`, rating)
  }

  // ---- money ----
  getLedger() { return this.request<OrgLedger>('/employer/ledger/') }
  listLedgerEntries() { return this.request<LedgerEntry[]>('/employer/ledger/entries/') }
  topUp(amount: string, method: string) {
    return this.post<{ ledger: OrgLedger; entry: LedgerEntry }>('/employer/ledger/topup/', { amount, method })
  }
  listInvoices() { return this.request<Invoice[]>('/employer/invoices/') }

  // ---- reference ----
  listSites() { return this.request<OrgSite[]>('/employer/sites/') }
  getSiteQr(siteId: string) {
    return this.request<SiteQr>(`/employer/sites/${siteId}/qr/`)
  }
  listSkills() { return this.request<Skill[]>('/employer/skills/') }
}
